import { GOOGLE_TTS_MODEL, GoogleVoiceOption, LanguageOption } from '../types';
import { dedupeVoices, groupVoicesByLanguage, hasVoicesInCatalog, toLanguageOptions } from './voiceCatalog';

const CACHE_VERSION = 4;
const CACHE_TTL_MS = 48 * 60 * 60 * 1000;

export interface GoogleVoiceCatalogCachePayload {
  version: number;
  model: string;
  fetchedAt: number;
  voices: GoogleVoiceOption[];
  languages?: LanguageOption[];
}

export interface GoogleVoiceCatalogCacheSnapshot {
  fetchedAt: number;
  isFresh: boolean;
  voicesByLanguage: Record<string, GoogleVoiceOption[]>;
  languageOptions: LanguageOption[];
}

export const getGoogleVoiceCatalogCacheKey = (model: string) => `google-ai-voice-catalog:v4:${model}`;

const storageAvailable = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const parsePayload = (raw: string | null, model: string): GoogleVoiceCatalogCacheSnapshot | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<GoogleVoiceCatalogCachePayload>;
    if (parsed.version !== CACHE_VERSION) return null;
    if (parsed.model !== model) return null;
    if (!Number.isFinite(parsed.fetchedAt)) return null;
    if (!Array.isArray(parsed.voices)) return null;

    const voices = dedupeVoices(
      parsed.voices
        .map((voice) => ({
          voiceId: String((voice as Partial<GoogleVoiceOption>)?.voiceId ?? '').trim(),
          displayName: String((voice as Partial<GoogleVoiceOption>)?.displayName ?? '').trim(),
          languageCode: String((voice as Partial<GoogleVoiceOption>)?.languageCode ?? '').trim(),
          gender: String((voice as Partial<GoogleVoiceOption>)?.gender ?? '').trim(),
        }))
        .filter((voice) => voice.voiceId && voice.languageCode),
    );

    const voicesByLanguage = groupVoicesByLanguage(voices);
    if (!hasVoicesInCatalog(voicesByLanguage)) return null;
    const languageOptions = Array.isArray(parsed.languages)
      ? parsed.languages
          .map((option) => ({
            code: String((option as Partial<LanguageOption>)?.code ?? '').trim(),
            label: String((option as Partial<LanguageOption>)?.label ?? '').trim(),
          }))
          .filter((option) => option.code.length > 0 && option.label.length > 0)
      : toLanguageOptions(voicesByLanguage);

    const fetchedAt = Number(parsed.fetchedAt);
    return {
      fetchedAt,
      isFresh: Date.now() - fetchedAt <= CACHE_TTL_MS,
      voicesByLanguage,
      languageOptions,
    };
  } catch {
    return null;
  }
};

export const readGoogleVoiceCatalogCache = (model: string = GOOGLE_TTS_MODEL): GoogleVoiceCatalogCacheSnapshot | null => {
  if (!storageAvailable()) return null;

  try {
    return parsePayload(window.localStorage.getItem(getGoogleVoiceCatalogCacheKey(model)), model);
  } catch {
    return null;
  }
};

export const writeGoogleVoiceCatalogCache = (
  voicesByLanguage: Record<string, GoogleVoiceOption[]>,
  languageOptions: LanguageOption[],
  model: string = GOOGLE_TTS_MODEL,
) => {
  if (!storageAvailable()) return;
  if (!hasVoicesInCatalog(voicesByLanguage)) return;

  const voices = dedupeVoices(Object.values(voicesByLanguage).flat());
  const payload: GoogleVoiceCatalogCachePayload = {
    version: CACHE_VERSION,
    model,
    fetchedAt: Date.now(),
    voices,
    languages: languageOptions,
  };

  try {
    window.localStorage.setItem(getGoogleVoiceCatalogCacheKey(model), JSON.stringify(payload));
  } catch {
    // no-op
  }
};
